import { Box, CircularProgress, Grid, Modal } from "@mui/material";
import React, { use, useEffect, useState } from "react";
import { colorConstant } from "../../../../styles/colorConstant/colorConstant";
import PosCustomIcon from "../../../../customComponents/PosCustomIcon/PosCustomIcon";
import { CalendarDaysIcon, CheckIcon, MapPinIcon, XMarkIcon } from "@heroicons/react/20/solid";
import "./LogisticsModal.css";
import twoWheelerIcon from "../../../../assets/images/two-wheeler.png";
import carIcon from "../../../../assets/images/car-icon.png";
import TruckIcon from "../../../../assets/images/truck-icon.png";
import { BoltIcon, ClockIcon, CubeIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import StyledDatePicker from "../../product/all-products/components/custom/StyledDatePicker";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { PosCustomButton } from "../../../../customComponents/PosCustomButton/PosCustomButton";
import {
  checkLogisticService,
  getOrderDetails,
  manualLogisticBooking,
} from "../../../../config/Services";
import { useSnackbar } from "../../product/hooks/SnackbarProvider";
import PosCustomTimeSelector from "../../../../customComponents/PosCustomTimeSelector/PosCustomTimeSelector";
import { v4 as uuidv4 } from "uuid";

const LogisticsModal = ({ isLogisticsModalOpen, setIsLogisticsModalOpen, item }) => {
  const style = {
    position: "absolute",
    top: 0,
    right: 0,
    width: "90%",
    height: "100vh",
    bgcolor: `${colorConstant.surfaceWhite}`,
    borderTopLeftRadius: "12px",
    borderBottomLeftRadius: "12px",
    display: "flex",
    flexDirection: "column",
    padding: "0.875rem",
  };

  const [pickupLocationObj, setPickupLocationObj] = useState({});
  const [pickupLocationString, setPickupLocationString] = useState("");
  const [dropLocationObj, setDropLocationObj] = useState({});
  const [dropLocationString, setDropLocationString] = useState("");
  const [time, setTime] = useState(null);
  const [findVehicleLoader, setFindVehicleLoader] = useState(false);
  const [vehicleData, setVehicleData] = useState([]);
  const [selectedVehicleOption, setSelectedVehicleOption] = useState(null);
  const [uuidStore, setUuidStore] = useState("");
  const [dropLocationLoading, setDropLocationLoading] = useState(false);
  const [bookingLoader, setBookingLoader] = useState(false);
  const [bookingDetails, setBookingDetails] = useState({
    vehicleType: "TWO_WHEELER",
    scheduleType: "IMMEDIATE",
    pickupDate: null,
    pickupTime: null,
  });

  const showSnackbar = useSnackbar();
  const locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");
  const locationData = JSON.parse(localStorage.getItem("locationData")) || {};

  const vehicleOptions = [
    { label: "2 Wheeler", value: "TWO_WHEELER", icon: twoWheelerIcon },
    { label: "3 Wheeler", value: "THREE_WHEELER", icon: carIcon },
  ];

  const handleModalClose = () => {
    setIsLogisticsModalOpen(false);
    setVehicleData([]);
    setUuidStore("");
  };

  //Storing Pickup Location
  const formatPickupLocation = () => {
    const address = locationData?.address;

    const pickupObj = {
      storeName: address?.name || "",
      addressLineOne: address?.addressLine1 || "",
      addressLineTwo: address?.addressLine2 || "",
      city: address?.city || "",
      state: address?.state || "",
      pincode: address?.pincode || "",
      contactNumber: address?.mobileNumber || "",
      latitude: address?.latitude || "",
      longitude: address?.longitude || "",
      country: address?.country || "",
    };

    // Save full object for API
    setPickupLocationObj(pickupObj);

    // Only include address-related fields for display
    const formattedAddress = [
      pickupObj.addressLineOne,
      pickupObj.addressLineTwo,
      pickupObj.city,
      pickupObj.country,
    ]
      .filter((value) => value && value.trim() !== "")
      .join(", ");

    setPickupLocationString(formattedAddress);
  };

  //Storing Drop Location
  const formatDropLocation = (address, customerName) => {
    const dropObj = {
      addressId: address?.addressId,
      name: customerName,
      addressLineOne: address?.addressLine1,
      addressLineTwo: address?.addressLine2,
      addressType: address?.addressType,
      city: address?.city,
      state: address?.state,
      pincode: address?.pinCode,
      country: address?.country,
      contactNumber: address?.phoneNo,
      latitude: address?.latitude,
      longitude: address?.longitude,
    };

    // Save full object for API
    setDropLocationObj(dropObj);

    // Only include address-related fields for display
    const formattedAddress = [
      dropObj.addressLineOne,
      dropObj.addressLineTwo,
      dropObj.city,
      dropObj.country,
    ]
      .filter((value) => value && value.trim() !== "")
      .join(", ");

    setDropLocationString(formattedAddress);
  };

  //Vehicle Selection
  const handleVehicleSelect = (type) => {
    setBookingDetails((prev) => ({
      ...prev,
      vehicleType: type,
    }));
    setVehicleData([]);
    setSelectedVehicleOption(null);
  };

  const handleScheduleSelect = (type) => {
    setBookingDetails((prev) => ({
      ...prev,
      scheduleType: type,
    }));
  };

  const convertStringTo24Hour = (timeString) => {
    if (!timeString) return null;

    const [time, period] = timeString.split(" ");
    let [hour, minute] = time.split(":");

    let hour24 = parseInt(hour, 10);

    if (period === "PM" && hour24 !== 12) {
      hour24 += 12;
    }

    if (period === "AM" && hour24 === 12) {
      hour24 = 0;
    }

    return `${hour24.toString().padStart(2, "0")}:${minute}`;
  };

  const formatToISODateTime = (date, time) => {
    if (!date || !time) return null;

    // Combine date + time
    const dateTimeString = `${date}T${time}:00`;

    // Convert to ISO format (UTC)
    const isoString = new Date(dateTimeString).toISOString();

    return isoString;
  };

  const formatSalesChannel = (channel) => {
    if (!channel) return "";
    const sourceChannelMap = {
      WHATSAPP: "whatsapp",
      WEB_APP: "web_app",
      ADHOC: "adhoc",
      SWIGGY: "swiggy",
      ZOMATO: "zomato",
      OTHER: "other",
      URBAN_PIPER: "urban_piper",
    };

    const formatSalesChannel = (channel) => {
      if (!channel) return "";

      const sourceChannelMap = {
        WHATSAPP: "whatsapp",
        WEB_APP: "web_app",
        ADHOC: "adhoc",
        SWIGGY: "swiggy",
        ZOMATO: "zomato",
        OTHER: "other",
        URBAN_PIPER: "urban_piper",
      };

      return sourceChannelMap[channel] || channel.toLowerCase();
    };
  };

  const findDeliveryPartners = () => {
    setFindVehicleLoader(true);
    const formattedPickupDateTime =
      bookingDetails.scheduleType === "SCHEDULED"
        ? formatToISODateTime(bookingDetails.pickupDate, bookingDetails.pickupTime)
        : null;

    const uuid = uuidv4();
    setUuidStore(uuid);

    const payload = {
      transactionId: uuid,
      clientOrderId: item?.orderId,
      vehicleType: bookingDetails?.vehicleType,
      pickupDate: formattedPickupDateTime,
      pickupTime: bookingDetails?.pickupTime,
      pickupAddress: {
        name: pickupLocationObj?.storeName,
        addressLineOne: pickupLocationObj?.addressLineOne,
        addressLineTwo: pickupLocationObj?.addressLineTwo,
        city: pickupLocationObj?.city,
        state: pickupLocationObj?.state,
        pincode: pickupLocationObj?.pincode,
        contactNumber: pickupLocationObj?.contactNumber,
        latitude: pickupLocationObj?.latitude,
        longitude: pickupLocationObj?.longitude,
      },
      dropAddress: {
        name: dropLocationObj?.name,
        addressLineOne: dropLocationObj?.addressLineOne,
        addressLineTwo: dropLocationObj?.addressLineTwo,
        city: dropLocationObj?.city,
        state: dropLocationObj?.state,
        pincode: dropLocationObj?.pincode,
        contactNumber: dropLocationObj?.contactNumber,
        latitude: dropLocationObj?.latitude,
        longitude: dropLocationObj?.longitude,
      },
    };
    checkLogisticService(payload)
      .then((res) => {
        if (res?.data?.data?.serviceable === false) {
          showSnackbar("Location not serviceable", "warning");
          setFindVehicleLoader(false);
          return;
        }
        setVehicleData(res?.data?.data);
        setFindVehicleLoader(false);
      })
      .catch((err) => {
        showSnackbar("Unable to Find Logistics Partners", "warning");
        setFindVehicleLoader(false);
      });
  };

  const handleBookLogistic = () => {
    setBookingLoader(true);
    const formattedPickupDateTime =
      bookingDetails.scheduleType === "SCHEDULED"
        ? formatToISODateTime(bookingDetails.pickupDate, bookingDetails.pickupTime)
        : null;

    const formattedSourceChannel = formatSalesChannel(item?.salesChannel);

    const payload = {
      locationId: locId,
      clientOrderId: item?.orderId,
      orgId: orgId,
      transactionId: uuidStore,
      pickupDate: formattedPickupDateTime,
      pickupTime: bookingDetails?.pickupTime,
      assignmentMode: "adhoc",
      selectedProvider: selectedVehicleOption?.provider,
      sourceChannel: formattedSourceChannel,
      cod: false,
      pickupAddress: {
        name: pickupLocationObj?.storeName,
        addressLineOne: pickupLocationObj?.addressLineOne,
        addressLineTwo: pickupLocationObj?.addressLineTwo,
        city: pickupLocationObj?.city,
        state: pickupLocationObj?.state,
        pincode: pickupLocationObj?.pincode,
        contactNumber: pickupLocationObj?.contactNumber,
        latitude: pickupLocationObj?.latitude,
        longitude: pickupLocationObj?.longitude,
      },
      dropAddress: {
        name: dropLocationObj?.name,
        addressLineOne: dropLocationObj?.addressLineOne,
        addressLineTwo: dropLocationObj?.addressLineTwo,
        city: dropLocationObj?.city,
        state: dropLocationObj?.state,
        pincode: dropLocationObj?.pincode,
        contactNumber: dropLocationObj?.contactNumber,
        latitude: dropLocationObj?.latitude || 13.0731171832,
        longitude: dropLocationObj?.longitude || 77.751078733,
      },
      scheduledTime: null,
      dropInstructions: null,
      pickupOtp: null,
      deliveryOtp: null,
      prepaid: false,
      prepTime: 0,
    };

    manualLogisticBooking(payload)
      .then((res) => {
        if (res?.data?.data?.es > 0) {
          showSnackbar(res?.data?.data?.message || "Unable to book the logistics", "error");
          setBookingLoader(false);
          return;
        }
        setBookingLoader(false);
        showSnackbar("Logistics successfully booked", "success");
        handleModalClose();
      })
      .catch((err) => {
        showSnackbar("Unable to book the logistics", "error");
        setBookingLoader(false);
      });
  };

  useEffect(() => {
    if (isLogisticsModalOpen) {
      formatPickupLocation();
      setDropLocationLoading(true);
      getOrderDetails(item?.orderId, locId)
        .then((res) => {
          const data = res?.data?.data;
          const customerName = data?.baseOrderResponse?.customerName;
          formatDropLocation(data?.addressEntityModel?.shippingAddress, customerName);
          setDropLocationLoading(false);
        })
        .catch((err) => {
          showSnackbar("Unable to fetch the address", "error");
          setDropLocationLoading(false);
        });
    }
  }, [isLogisticsModalOpen]);

  return (
    <Modal open={isLogisticsModalOpen} onClose={handleModalClose}>
      <Box sx={style}>
        <div className="logistic-header-section">
          <div className="logistic-modal-header">
            <p>New Booking</p>
            <PosCustomIcon
              icon={<XMarkIcon />}
              height={"20px"}
              width={"20px"}
              pointer={true}
              onClickFunction={handleModalClose}
            />
          </div>
          <div className="logistic-modal-subheader">
            <p>Compare rates and book delivery partners</p>
          </div>
        </div>

        <div className="logistic-modal-body">
          <div className="logistic-modal-content">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <div className="logistic-modal-left-container">
                  <div className="logistic-modal-address">
                    <div className="location-indicator">
                      <div className="pickup-outer-dot">
                        <div className="pickup-inner-dot"></div>
                      </div>
                      <div className="location-dash-line"></div>
                      <div className="drop-dot">
                        <PosCustomIcon
                          icon={<MapPinIcon />}
                          height={"13px"}
                          width={"13px"}
                          color="var(--surface-white)"
                        />
                      </div>
                    </div>

                    <div className="location-inputs">
                      <div className="logistic-modal-address-pickup">
                        <p>Pickup Location</p>
                        <input
                          type="text"
                          className="logistic-pickup-input"
                          value={pickupLocationString}
                          disabled
                          placeholder="Enter Pickup Location"
                        />
                      </div>

                      <div className="logistic-modal-address-drop">
                        <p>Drop Location*</p>
                        {/* <input
                          type="text"
                          className="logistic-pickup-input"
                          value={dropLocationString}
                          disabled
                          placeholder="Enter Pickup Location"
                        /> */}
                        <div className="logistic-input-wrapper">
                          <input
                            type="text"
                            className="logistic-pickup-input"
                            value={dropLocationString}
                            disabled
                            placeholder={
                              dropLocationLoading ? "Fetching address..." : "Enter Drop Location"
                            }
                          />
                          <div className="loaderInsideInput">
                            {dropLocationLoading && (
                              <CircularProgress
                                color="success"
                                sx={{ color: "#5271ff" }}
                                size={16}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="logistic-card-container">
                    <p>Vehicle Type*</p>
                    <div className="logistic-card-wrapper">
                      {vehicleOptions.map((vehicle) => (
                        <div
                          key={vehicle.value}
                          className={`logistic-vehicle-card ${
                            bookingDetails.vehicleType === vehicle.value ? "selected-vehicle" : ""
                          }`}
                          onClick={() => handleVehicleSelect(vehicle.value)}
                        >
                          <PosCustomIcon image={vehicle.icon} height={"20px"} width={"20px"} />
                          <p>{vehicle.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="logistic-schedule-container">
                    <p>Schedule*</p>
                    <div className="logistic-schedule-slot-container">
                      <div
                        className={`logistic-schedule-button-wrapper ${
                          bookingDetails.scheduleType === "IMMEDIATE" ? "selected-schedule" : ""
                        }`}
                        onClick={() => handleScheduleSelect("IMMEDIATE")}
                      >
                        <p>Immediate Pickup</p>
                        <PosCustomIcon
                          icon={<BoltIcon />}
                          height={"20px"}
                          width={"20px"}
                          color={
                            bookingDetails.scheduleType === "IMMEDIATE" ? "#0860E6" : "#717681"
                          }
                        />
                      </div>

                      <div
                        className={`logistic-schedule-button-wrapper ${
                          bookingDetails.scheduleType === "SCHEDULED" ? "selected-schedule" : ""
                        }`}
                        onClick={() => handleScheduleSelect("SCHEDULED")}
                      >
                        <p>Scheduled Pickup</p>
                        <PosCustomIcon
                          icon={<CalendarDaysIcon />}
                          height={"20px"}
                          width={"20px"}
                          color={
                            bookingDetails.scheduleType === "SCHEDULED" ? "#0860E6" : "#717681"
                          }
                        />
                      </div>
                    </div>

                    {bookingDetails.scheduleType === "SCHEDULED" && (
                      <div className="logistic-date-picker-container">
                        <div className="logistic-date-picker-wrapper">
                          <p>Pickup Date*</p>
                          <input
                            type="date"
                            className="logistic-date-input"
                            placeholder="dd/mm/yyyy"
                            value={bookingDetails.pickupDate}
                            onChange={(e) => {
                              setBookingDetails((prev) => ({
                                ...prev,
                                pickupDate: e.target.value,
                              }));
                            }}
                          />
                        </div>

                        <div className="logistic-date-picker-wrapper">
                          <p>Pickup Time*</p>
                          <PosCustomTimeSelector
                            value={time}
                            onChange={(selectedTime) => {
                              setTime(selectedTime);
                              const time24 = convertStringTo24Hour(selectedTime);
                              setBookingDetails((prev) => ({
                                ...prev,
                                pickupTime: time24,
                              }));
                            }}
                            dropdownPosition="top"
                            width="100%"
                            padding="6px 10px"
                            radius="6px"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="logistic-action-button">
                    <PosCustomButton
                      title="Find Delivery Partners"
                      variant="blue-d"
                      width="100%"
                      p="12px 16px"
                      radius="6px"
                      iconOnLeft={
                        <PosCustomIcon
                          icon={<MagnifyingGlassIcon />}
                          height={"16px"}
                          width={"16px"}
                          color="var(--surface-white)"
                        />
                      }
                      onClickFunction={findDeliveryPartners}
                      loading={findVehicleLoader}
                      disabled={dropLocationLoading}
                    />
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div className="logistic-modal-right-container">
                  {vehicleData?.vehicleOptions ? (
                    <div className="logistic-right-vehicle-container">
                      <div className="logistic-rigt-container-header">
                        <p>
                          Available Partners{" "}
                          {`(${
                            vehicleData?.vehicleOptions?.[bookingDetails?.vehicleType]?.length
                          })`}
                        </p>
                      </div>
                      {vehicleData?.vehicleOptions?.[bookingDetails?.vehicleType]?.map(
                        (vehicle, idx) => {
                          const isSelected = selectedVehicleOption?.provider === vehicle?.provider;
                          return (
                            <div
                              key={idx}
                              className={`logistic-vehicle-wrapper ${
                                isSelected ? "selected-delivery-partner" : ""
                              }`}
                              onClick={() => setSelectedVehicleOption(vehicle)}
                            >
                              <div className="logistic-provider-logo">
                                <p className="logistic-vehicle-logo-name">{vehicle?.provider}</p>
                              </div>

                              <div className="logistic-provider-outer-wrapper">
                                <div className="logistic-provider-wrapper">
                                  <p className="logistic-vehicle-porvider">{vehicle?.provider}</p>
                                  <p className="logistic-vehicle-cost">₹{vehicle?.deliveryCost}</p>
                                </div>
                                <div className="logistic-vehicle-eta">
                                  <PosCustomIcon
                                    icon={<ClockIcon />}
                                    height={"14px"}
                                    width={"14px"}
                                    color="#535861"
                                  />
                                  <p>{vehicle?.pickupEtaMinutes || "N/A"}</p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  ) : (
                    <div className="logistic-right-empty-container">
                      <PosCustomIcon
                        icon={<CubeIcon />}
                        height={"22px"}
                        width={"22px"}
                        color="#717681"
                      />
                      <p className="logistic-empty-header">Waiting for Details</p>
                      <div className="logistic-empty-subheading-container">
                        <p>
                          Enter pickup and drop locations to fetch real-time quotes from ONDC
                          partners.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </Grid>
            </Grid>
          </div>
        </div>

        <div className="logistic-modal-footer">
          <PosCustomButton
            title="Book"
            variant="blue-d"
            width="max-content"
            p="8px 12px"
            radius="6px"
            disabled={!selectedVehicleOption}
            iconOnRight={
              <PosCustomIcon
                icon={<CheckIcon />}
                height={"16px"}
                width={"16px"}
                color="var(--surface-white)"
              />
            }
            onClickFunction={handleBookLogistic}
            loading={bookingLoader}
          />
        </div>
      </Box>
    </Modal>
  );
};

export default LogisticsModal;
